import React from "react";
import { Flex, Text, Button, Select, FileObjectType } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import * as uploadStyles from "../styles_vanilla.css";
import { getDataTypeName } from "../utils";

interface FileCardProps {
  file: FileObjectType;
  dataType?: string;
  availableDataTypes: Array<{ value: string; label: string }>;
  onDataTypeChange: (fileName: string, dataType: string | number | string[] | number[]) => void;
  onRemoveFile: (fileName: string) => void;
}

const FileCard: React.FC<FileCardProps> = ({
  file,
  dataType,
  availableDataTypes,
  onDataTypeChange,
  onRemoveFile,
}) => {
  return (
    <Flex
      className={dataType ? uploadStyles.fileCardReady : uploadStyles.fileCard}
      gap={Sizes.MD}
      alignItems="flex-start"
    >
      <Flex
        className={
          dataType ? uploadStyles.fileIconContainerReady : uploadStyles.fileIconContainer
        }
      >
        <Icon
          type="file-alt"
          size="x-large"
          className={dataType ? uploadStyles.fileIconReady : uploadStyles.fileIconDefault}
        />
        {!dataType && <Flex className={uploadStyles.fileIconBadge} />}
      </Flex>

      <Flex flexDirection="column" gap={Sizes.SM} className={uploadStyles.fileContentSection}>
        <Flex justifyContent="space-between" alignItems="flex-start">
          <Flex flexDirection="column" gap={Sizes.XS}>
            <Text variant="strong-xl" color={Colors.Neutral900}>
              {file.file.name}
            </Text>
            <Text variant="regular-md" className={uploadStyles.fileMetadataText}>
              {(file.file.size / 1024).toFixed(1)} KB •{" "}
              {file.file.name.toLowerCase().endsWith(".tsv") ? "TSV" : "CSV"} File
            </Text>
          </Flex>
          <Button
            variant="neutral"
            icon="times"
            className={uploadStyles.removeButton}
            onClick={() => onRemoveFile(file.file.name)}
          ></Button>
        </Flex>

        {dataType ? (
          <Flex alignItems="center" gap={Sizes.XS}>
            <Text variant="regular-md" color={Colors.Neutral900}>
              {getDataTypeName(dataType)}
            </Text>
            <Flex alignItems="center" gap={Sizes.XS}>
              <Icon type="check" size="small" color={Colors.Success400} />
              <Text variant="regular-md" className={uploadStyles.readyStatusText}>
                Ready
              </Text>
            </Flex>
          </Flex>
        ) : (
          <Flex className={uploadStyles.actionRequired} alignItems="center" gap={Sizes.XS}>
            <Icon type="exclamation-triangle" size="medium" color={Colors.Warning400} />
            <Text variant="regular-md" className={uploadStyles.actionRequiredText}>
              Action Required
            </Text>
          </Flex>
        )}

        <Flex className={uploadStyles.dataTypeSection} flexDirection="column" gap={Sizes.SM}>
          <Text variant="strong-sm" color={Colors.Neutral900}>
            What type of data does this file contain?
          </Text>
          <Select
            value={dataType || ""}
            onChange={(value) => onDataTypeChange(file.file.name, value)}
            placeholder="Choose data type..."
            full
            className={uploadStyles.dataTypeSelect}
          >
            <Select.Section>
              {availableDataTypes.map((dataTypeOption) => (
                <Select.Option key={dataTypeOption.value} value={dataTypeOption.value}>
                  {dataTypeOption.label}
                </Select.Option>
              ))}
            </Select.Section>
          </Select>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default FileCard;
